import React from 'react';
import { Box } from '@mui/material';

/**
 * Renders the user's uploaded image with layered shimmer, glow-pulse, and
 * reveal-sweep effects — a "magic processing" visual à la Canva / ChatGPT.
 *
 * The component fills 100 % of its parent; sizing is controlled externally so
 * it scales automatically if the card ever grows.
 *
 * @param {string}  previewUrl  – blob URL from URL.createObjectURL(file)
 * @param {number}  progress    – 0-100 upload progress (drives blur fade)
 */
const ImageProcessingVisual = ({ previewUrl, progress = 0 }) => {
  const blurPx = Math.max(0, 10 - (progress / 100) * 10);

  return (
    <Box
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        borderRadius: '12px',
        overflow: 'hidden',
        isolation: 'isolate',
      }}
    >
      {/* Layer 1 — user's image, blur driven by upload progress */}
      <Box
        component="img"
        src={previewUrl}
        alt=""
        sx={{
          display: 'block',
          width: '100%',
          height: '100%',
          objectFit: 'cover',
          filter: `blur(${blurPx}px) saturate(${0.55 + (progress / 100) * 0.45})`,
          transition: 'filter 600ms ease-out',
        }}
      />

      {/* Layer 2 — diagonal shimmer sweep — GPU-composited via transform, no paint per frame */}
      <Box
        aria-hidden="true"
        sx={{
          position: 'absolute',
          inset: 0,
          overflow: 'hidden',
          pointerEvents: 'none',
          borderRadius: 'inherit',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            left: '100%',
            width: '100%',
            height: '100%',
            background: `linear-gradient(
              105deg,
              transparent 30%,
              rgba(255,255,255,0.35) 46%,
              rgba(255,255,255,0.55) 50%,
              rgba(255,255,255,0.35) 54%,
              transparent 70%
            )`,
            animation: 'mfImageShimmer 2.4s ease-in-out infinite',
            willChange: 'transform',
            '@keyframes mfImageShimmer': {
              '0%':   { transform: 'translateX(0)' },
              '100%': { transform: 'translateX(-200%)' },
            },
          }}
        />
      </Box>

    </Box>
  );
};

export default ImageProcessingVisual;
